import { VesselData } from "../vessel-reg/vessel-data.model";
import { FishTicket } from "./delivery-fish-ticket.model";
import { LookupItem } from "./lookup-item.model";
import { TrawlSet } from "./trawl-set.model";
import { DeliveryStatus } from "./trip.model";

/**
 * Delivery model for Trawl
 */
export class TrawlDelivery {

  status: DeliveryStatus = DeliveryStatus.New;

  //Links to operatorUserID in OperatorUser model
  skipperUserID: string = '';

  //From registration, or if joint then section from registration
  operatorName: string = '';

  //For vessel name and number - Default to last entered
  vessel: VesselData = new VesselData('', '');

  crewSize!: number;

  //Unique identifier for each delivery
  deliveryID: string = '';

  departureDate!: Date;

  departurePort: LookupItem = new LookupItem('', 0);

  returnDate!: Date;

  returnPort: LookupItem = new LookupItem('', 0);

  //List of associated Sets
  sets: TrawlSet[] = [];

  //Once received, this delivery will get a logbook id and received date, this received date is the original submission date
  logbookId!: string;
  receivedDate!: Date;

  //Logbook version starts at 1, Incremented upon each submission of edits
  //submissionDate is the date of the most recently submitted edits
  logbookVersion: number = 1;
  submissionDate!: Date;

  atSeaDelivery: boolean = false;

  //Can there be multiple mothership deliveries? Can it be to different motherships?
  mothershipName: string = "";

  //All below default to last entered
  isEFP: boolean = false;

  isEMTrip: boolean = false;

  isObservedTrip: boolean = false;

  //Mimics the PSMFC schema, multiple deliveries can be linked to a landing, each delivery is basically just a fish ticket
  tickets: FishTicket[] = [];

  comments: string = '';

  //Pending Delivery use only, true if a new delivery is started and fields should be filled to default values
  isNewDelivery: boolean = true;

  //For PSMFC server, set on submit - Supposed to be a GUID, using https://www.npmjs.com/package/guid-typescript
  localLandingGUID!: string;



  /**
   * On every update from the delivery detail page, this method is called to update {@link TrawlDeliveryComponent.landing}   * 
   * @param form Landing data
   */
  updateValue(form: any) {
    this.vessel = form.vessel;
    this.crewSize = form.crewSize
    this.operatorName = form.operator;
    this.departureDate = form.departureDate ? new Date(form.departureDate) : this.departureDate;
    this.departurePort = form.departurePort;
    this.returnDate = form.returnDate ? new Date(form.returnDate) : this.returnDate;
    this.returnPort = form.returnPort;
    this.atSeaDelivery = form.atSeaDelivery;
    this.mothershipName = form.mothershipName;
    this.isEFP = form.isExemptedFP;
    this.isEMTrip = form.isEMTrip;
    this.isObservedTrip = form.isObservedTrip;
    this.comments = form.comments;

  }


  setValues(delivery: any) {
    let d = delivery as TrawlDelivery;
    this.skipperUserID = d.skipperUserID;
    this.operatorName = d.operatorName;
    this.vessel = new VesselData(d.vessel.vesselName, d.vessel.vesselCoastNumber);
    this.crewSize = d.crewSize
    this.deliveryID = d.deliveryID;
    this.atSeaDelivery = d.atSeaDelivery;
    this.mothershipName = d.mothershipName;
    this.isEFP = d.isEFP;
    this.isEMTrip = d.isEMTrip;
    this.isObservedTrip = d.isObservedTrip;
    this.departureDate = d.departureDate ? new Date(d.departureDate) : this.departureDate;
    this.departurePort = LookupItem.transformAnonymous(d.departurePort);
    this.returnDate = d.returnDate ? new Date(d.returnDate) : this.returnDate;
    this.returnPort = LookupItem.transformAnonymous(d.returnPort);
    this.sets = d.sets.map(set => TrawlSet.transformAnonymous(set));
    this.tickets = d.tickets;
    this.isNewDelivery = d.isNewDelivery;
    this.logbookId = d.logbookId;
    this.receivedDate = new Date(d.receivedDate);
    this.localLandingGUID = d.localLandingGUID;
    this.logbookVersion = d.logbookVersion ?? 1;
    this.submissionDate = d.submissionDate;
    this.comments = d.comments;
  }


  static transformAnonymous(anon: any): TrawlDelivery {
    let td = new TrawlDelivery();
    td.status = anon.status ?? DeliveryStatus.New;
    td.skipperUserID = anon.skipperUserID;
    td.operatorName = anon.operatorName;
    td.vessel = new VesselData(anon.vessel.vesselName, anon.vessel.vesselCoastNumber);
    td.crewSize = anon.crewSize;
    td.deliveryID = anon.deliveryID;
    if (anon.departureDate instanceof Date && isNaN(anon.departureDate)) anon.departureDate = undefined;
    td.departureDate = anon.departureDate ? new Date(anon.departureDate) : anon.departureDate;
    td.departurePort = LookupItem.transformAnonymous(anon.departurePort);
    if (anon.returnDate instanceof Date && isNaN(anon.returnDate)) anon.returnDate = undefined;
    td.returnDate = anon.returnDate ? new Date(anon.returnDate) : anon.returnDate;
    td.returnPort = LookupItem.transformAnonymous(anon.returnPort);
    td.sets = (anon.sets as any[]).map(set => TrawlSet.transformAnonymous(set));
    td.logbookId = anon.logbookId;
    td.receivedDate = anon.receivedDate;
    td.logbookVersion = anon.logbookVersion;
    td.submissionDate = anon.submissionDate;
    td.atSeaDelivery = anon.atSeaDelivery;
    td.mothershipName = anon.mothershipName;
    td.isEFP = anon.isEFP;
    td.isEMTrip = anon.isEMTrip;
    td.isObservedTrip = anon.isObservedTrip;
    td.tickets = anon.tickets;
    td.isNewDelivery = anon.isNewDelivery;
    td.localLandingGUID = anon.localLandingGUID;
    td.comments = anon.comments;
    return td;
  }
}
